<template>
  <div>
    <div class="autocomplete">
      <input
        type="text"
        v-model="inputValue"
        @input="updateSuggestions"
        @keydown.down.prevent="navigateSuggestions(1)"
        @keydown.up.prevent="navigateSuggestions(-1)"
        @keydown.enter.prevent="selectSuggestion(null)"
      />
      <ul v-if="filteredSuggestions.length > 0" class="suggestions">
        <li
          v-for="(suggestion, index) in filteredSuggestions"
          :key="index"
          :class="{ highlighted: index === selectedIndex }"
          @click="selectSuggestion(suggestion)"
        >
          {{ suggestion }}
        </li>
      </ul>
    </div>
    <div v-if="topSimilarities.length > 0" class="top-similarities">
      <h3>Most Similar Municipalities To {{ selectedMunicipality }}:</h3>
      <ul>
        <li v-for="(similarity, index) in topSimilarities" :key="index">
          {{ getMunicipalityNameFromServer(similarity.target_municipality) }}: {{ similarity.similarity.toFixed(2) }}
        </li>
      </ul>
    </div>
    <l-map
      class="map"
      style="height: 80vh; width: 100%;"
      :zoom="zoom"
      :center="center"
    >
      <l-tile-layer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      ></l-tile-layer>

      <l-geo-json
        v-for="(feature, index) in geojsonFeatures"
        :key="geojsonKey + '-' + index"
        :geojson="feature"
        :options-style="getFeatureStyle"
        @click="onFeatureClick(feature)"
      >
        <l-tooltip
          :content="getTooltipContent(feature)"
          :permanent="false"
          :sticky="true"
        />
      </l-geo-json>
    </l-map>
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>
  </div>
</template>
  
  <script>
  /* eslint-disable */
  import { LMap, LTileLayer, LGeoJson, LTooltip  } from "@vue-leaflet/vue-leaflet";
  import "leaflet/dist/leaflet.css";
  import axios from "axios";
    
  export default {
    name: "Heatmap",
    components: { LMap, LTileLayer, LGeoJson, LTooltip },
    data() {
      return {
        selectedMunicipality: null,
        center: [46.8182, 8.2275], // Center of Switzerland
        zoom: 9,
        geojsonFeatures: [], // GeoJSON data for municipalities
        similarities: [],
        geojsonKey: 0,
        inputValue: "",
        suggestions: [],
        filteredSuggestions: [],
        selectedIndex: -1,
        isLoading: false,
      };
    },
    computed: {
      topSimilarities() {
        if (!this.similarities || this.similarities.length === 0) return [];
        return [...this.similarities]
          .sort((a, b) => b.similarity - a.similarity)
          .slice(1, 6);
      },
    },
    async mounted() {
        this.isLoading = true;
        const response = await fetch("/gemeinden.geojson"); // Ensure the file is in your `public` folder or copied to `dist`
        const geojsonData = await response.json();
        this.geojsonFeatures = geojsonData.features;
        this.initSuggestions()
        this.isLoading = false;
  },
    methods: {
      onFeatureClick(feature) {
        const municipalityName =this.getMunicipalityNameFromGeoJson(feature.properties);
        if (municipalityName) {
            this.selectedMunicipality = municipalityName;
            this.fetchSimilarities();
        } else {
            console.error("Municipality name not found in feature properties.");
        }
    },
    async fetchSimilarities() {
        this.isLoading = true;
        if (!this.selectedMunicipality) return;

        try {
          const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/similarities/${encodeURIComponent(this.selectedMunicipality)}`
          );

        this.similarities = response.data;

        // Update GeoJSON features with similarity values
        this.updateHeatmap();
        this.isLoading = false;
        } catch (error) {
          console.error("Failed to fetch similarities:", error);
          this.isLoading = false;
        }
    },
      updateHeatmap() {
        this.geojsonFeatures.forEach((feature) => {
          const name = this.getMunicipalityNameFromGeoJson(feature.properties);
          feature.properties.similarity =
            this.similarities.find((s) => 
            s.target_municipality === name || 
            s.target_municipality === feature.properties["gemeinde.NAME"])
            ?.similarity || 0;
        });
        this.geojsonKey++;
      },
      getFeatureStyle(feature) {
        if (this.getMunicipalityNameFromGeoJson(feature.properties) === this.selectedMunicipality){
          return { fillColor: "#D62728", color: "#000", weight: 1, fillOpacity: 0.7 };  
        }

        const similarity = feature.properties.similarity ?? 0;
        const color = this.getColor(similarity);
        return { fillColor: color, color: "#000", weight: 1, fillOpacity: 0.7 };
      },
      getMunicipalityNameFromGeoJson(properties){
        return `${properties["gemeinde.NAME"]} (${properties["kanton.KUERZEL"]})`;
      },
      getMunicipalityNameFromServer(municipality) {
        const feature = this.geojsonFeatures.find(f => f.properties["gemeinde.NAME"] === municipality);
        return feature 
            ? `${feature.properties["gemeinde.NAME"]} (${feature.properties["kanton.KUERZEL"]})` 
            : municipality;
      },
      getColor(value) {
        // Remap value from [-1, 1] to [0, 1]
        const normalizedValue = (value + 1) / 2;

        // Ensure value is clamped between 0 and 1
        const clampedValue = Math.min(Math.max(normalizedValue, 0), 1);

        // Viridis gradient color stops
        const viridisColors = [
            "#440154", // Purple (low)
            "#3b528b", // Blue
            "#21918c", // Teal
            "#5dc963", // Green
            "#fde725"  // Yellow (high)
        ];

        // Compute the color index based on value
        const index = Math.floor(clampedValue * (viridisColors.length - 1));
        const nextIndex = Math.min(index + 1, viridisColors.length - 1);

        // Linear interpolation between two closest colors
        const ratio = clampedValue * (viridisColors.length - 1) - index;
        return this.interpolateColor(viridisColors[index], viridisColors[nextIndex], ratio);
      },
        // Helper function for linear interpolation
      interpolateColor(color1, color2, ratio) {
        const hexToRgb = (hex) => hex.match(/\w\w/g).map((x) => parseInt(x, 16));
        const rgbToHex = ([r, g, b]) =>
            `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;

        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        const interpolatedRgb = rgb1.map((v, i) =>
            Math.round(v + ratio * (rgb2[i] - v))
        );

        return rgbToHex(interpolatedRgb);
      },
      initSuggestions(){
          this.geojsonFeatures.forEach(feature => {
            this.suggestions.push(this.getMunicipalityNameFromGeoJson(feature.properties))
          });
      },
      updateSuggestions() {
        this.filteredSuggestions = this.suggestions.filter((item) =>
          item.toLowerCase().includes(this.inputValue.toLowerCase())
        );
        this.selectedIndex = -1; // Reset index
      },
      navigateSuggestions(direction) {
        if (this.filteredSuggestions.length === 0) return;

        this.selectedIndex =
          (this.selectedIndex + direction + this.filteredSuggestions.length) %
          this.filteredSuggestions.length;
        this.inputValue = this.filteredSuggestions[this.selectedIndex];
      },
      selectSuggestion(suggestion = null) {
        if (this.selectedIndex == -1) this.selectedIndex = 0;
        this.inputValue = suggestion || this.filteredSuggestions[this.selectedIndex];
        this.filteredSuggestions = []; // Clear suggestions
        this.selectedMunicipality = this.inputValue;
        this.fetchSimilarities()
      },
      getTooltipContent(feature) {
        const name = this.getMunicipalityNameFromGeoJson(feature.properties);
        const similarity = feature.properties.similarity ?? 0;
        return `${name}<br />Similarity: ${similarity.toFixed(2)}`;
      },
    },
  };
  </script>
  
  <style>
  .map{
    z-index:1;
  }
  .autocomplete {
    position: relative;
    width: 300px;
  }
  .suggestions {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid #ccc;
    max-height: 150px;
    overflow-y: auto;
    position: absolute;
    background: #fff;
    width: 100%;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 2;
  }
  .suggestions li {
    padding: 8px;
    cursor: pointer;
  }
  .suggestions li.highlighted {
    background-color: #f0f0f0;
  }
  .loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7); /* Semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; /* Ensure it sits on top */
  color: white;
  flex-direction: column;
}

.loading-spinner {
  border: 8px solid rgba(255, 255, 255, 0.3); /* Light outer ring */
  border-top: 8px solid white; /* Bright top */
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite; /* Spin animation */
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.top-similarities {
  position: absolute;
  top: 50px;
  right: 10px; 
  width: 250px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f9f9f9;
  z-index: 2;
}

.top-similarities h3 {
  margin-bottom: 10px;
  font-size: 1.2em;
}

.top-similarities ul {
  list-style: none;
  padding: 0;
}

.top-similarities li {
  margin: 5px 0;
  font-size: 1em;
}
  </style>
  